import React, { useEffect, useState } from "react";
import { http } from "../../lib/http";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Card } from "../../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Switch } from "../../components/ui/switch";
import { Plus, Pencil, Archive, Trash2, ArchiveRestore } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../contexts/AuthContext";
import { PageHeader, PageBody } from "../../components/shell/PageChrome";
import StatusBadge from "../../components/StatusBadge";
import ConfirmDialog from "../../components/data/ConfirmDialog";

export default function AdminRoomsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rooms, setRooms] = useState([]);
  const [classCounts, setClassCounts] = useState({});
  const [editing, setEditing] = useState(null);    // room being edited or null
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState(null);     // {action, room}

  const load = async () => {
    const r = await http.get("/rooms");
    const items = r.data.items || [];
    setRooms(items);
    // # of active classes referencing each room
    const c = await http.get("/classes?status=active&limit=1000").catch(() => ({ data: { items: [] } }));
    const counts = {};
    for (const cls of (c.data.items || c.data || [])) {
      counts[cls.room_id] = (counts[cls.room_id] || 0) + 1;
    }
    setClassCounts(counts);
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing({ name: "", capacity: 12, color: "#0A1F44", notes: "", active: true }); setOpen(true); };
  const openEdit = (r) => { setEditing({ ...r }); setOpen(true); };

  const save = async () => {
    try {
      if (editing.id) {
        await http.patch(`/rooms/${editing.id}`, {
          name: editing.name, capacity: editing.capacity, color: editing.color,
          notes: editing.notes, active: editing.active,
        });
        toast.success("Room updated");
      } else {
        await http.post("/rooms", {
          name: editing.name, capacity: editing.capacity, color: editing.color,
          notes: editing.notes,
        });
        toast.success("Room created");
      }
      setOpen(false); await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const archive = async (r) => {
    try {
      await http.patch(`/rooms/${r.id}`, { active: false });
      toast.success("Room archived"); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const unarchive = async (r) => {
    try {
      await http.patch(`/rooms/${r.id}`, { active: true });
      toast.success("Room reactivated"); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const hardDelete = async (r) => {
    if ((classCounts[r.id] || 0) > 0) {
      toast.error("Cannot delete — room is referenced by active classes. Archive instead.");
      return;
    }
    try {
      await http.delete(`/rooms/${r.id}`);
      toast.success("Room deleted"); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid="admin-rooms-page">
      <PageHeader
        title="Rooms"
        subtitle="Manage classrooms (Aulas). Renaming a room flows to all linked classes."
        action={isAdmin ? (
          <Button className="bg-[#0A1F44]" onClick={openCreate} data-testid="rooms-add-btn">
            <Plus className="h-4 w-4 mr-1"/>Add room
          </Button>
        ) : null}
      />
      <PageBody>
        <Card className="bg-white p-0 overflow-hidden">
          <table className="w-full text-sm" data-testid="rooms-table">
            <thead className="text-xs uppercase tracking-wider text-slate-500 border-b">
              <tr>
                <th className="text-left p-3">Name</th>
                <th className="text-left p-3">Capacity</th>
                <th className="text-left p-3">Colour</th>
                <th className="text-left p-3">Active classes</th>
                <th className="text-left p-3">Status</th>
                <th className="p-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rooms.length === 0 && (
                <tr><td colSpan={6} className="text-center p-6 text-slate-400">No rooms yet.</td></tr>
              )}
              {rooms.map(r => (
                <tr key={r.id} className="border-b hover:bg-slate-50" data-testid={`room-row-${r.id}`}>
                  <td className="p-3 font-medium">{r.name}</td>
                  <td className="p-3 text-slate-600">{r.capacity}</td>
                  <td className="p-3">
                    <span className="inline-block w-5 h-5 rounded border" style={{ backgroundColor: r.color || "#cbd5e1" }} title={r.color}/>
                  </td>
                  <td className="p-3 text-slate-600">{classCounts[r.id] || 0}</td>
                  <td className="p-3">
                    <StatusBadge status={r.active !== false ? "active" : "archived"} />
                  </td>
                  <td className="p-3 text-right">
                    {isAdmin && (
                      <div className="inline-flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => openEdit(r)} data-testid={`room-edit-${r.id}`} aria-label="Edit">
                          <Pencil className="h-4 w-4"/>
                        </Button>
                        {r.active !== false ? (
                          <Button size="sm" variant="ghost" onClick={() => setConfirm({ action: "archive", room: r })}
                            data-testid={`room-archive-${r.id}`} aria-label="Archive">
                            <Archive className="h-4 w-4 text-amber-700"/>
                          </Button>
                        ) : (
                          <Button size="sm" variant="ghost" onClick={() => unarchive(r)}
                            data-testid={`room-unarchive-${r.id}`} aria-label="Unarchive">
                            <ArchiveRestore className="h-4 w-4 text-emerald-700"/>
                          </Button>
                        )}
                        <Button size="sm" variant="ghost"
                          disabled={(classCounts[r.id] || 0) > 0}
                          title={(classCounts[r.id] || 0) > 0 ? "Cannot delete — referenced by active classes" : "Delete permanently"}
                          onClick={() => setConfirm({ action: "delete", room: r })}
                          data-testid={`room-delete-${r.id}`} aria-label="Delete">
                          <Trash2 className="h-4 w-4 text-rose-700"/>
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-w-md" data-testid="room-edit-dialog">
            <DialogHeader>
              <DialogTitle>{editing?.id ? "Edit room" : "Add room"}</DialogTitle>
            </DialogHeader>
            {editing && (
              <div className="space-y-3 text-sm">
                <div>
                  <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">Name</label>
                  <Input value={editing.name} onChange={e => setEditing({...editing, name: e.target.value})}
                    data-testid="room-name-input"/>
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">Capacity</label>
                  <Input type="number" value={editing.capacity}
                    onChange={e => setEditing({...editing, capacity: parseInt(e.target.value) || 0})}/>
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">Colour</label>
                  <div className="flex items-center gap-2">
                    <input type="color" value={editing.color || "#0A1F44"}
                      onChange={e => setEditing({...editing, color: e.target.value})}
                      className="h-9 w-12 rounded border" data-testid="room-colour-input"/>
                    <Input value={editing.color || ""}
                      onChange={e => setEditing({...editing, color: e.target.value})}
                      placeholder="#0A1F44" className="font-mono text-xs"/>
                  </div>
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">Notes</label>
                  <Textarea value={editing.notes || ""}
                    onChange={e => setEditing({...editing, notes: e.target.value})}/>
                </div>
                {editing.id && (
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Switch checked={editing.active !== false}
                      onCheckedChange={(v) => setEditing({...editing, active: v})}
                      data-testid="room-active-switch"/>
                    <span>Active</span>
                  </label>
                )}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button className="bg-[#0A1F44]" onClick={save} data-testid="room-save-btn">
                {editing?.id ? "Save changes" : "Create room"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <ConfirmDialog
          open={confirm?.action === "archive"}
          onCancel={() => setConfirm(null)}
          onConfirm={() => { archive(confirm.room); setConfirm(null); }}
          title="Archive room?"
          message={`"${confirm?.room?.name}" will be hidden from selectors. Existing classes keep their room reference. You can reactivate it later.`}
          confirmText="Archive"
          testid="confirm-archive-room"
        />
        <ConfirmDialog
          open={confirm?.action === "delete"}
          onCancel={() => setConfirm(null)}
          onConfirm={() => { hardDelete(confirm.room); setConfirm(null); }}
          title="Delete room permanently?"
          message={`This cannot be undone. "${confirm?.room?.name}" will be removed.`}
          confirmText="Delete permanently"
          danger={true}
          testid="confirm-delete-room"
        />
      </PageBody>
    </div>
  );
}
